import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { I18n } from 'react-redux-i18n'
import {
  daysConversionKey,
  daysInventoryKey,
  daysPayableKey,
  daysSaleKey,
} from '..'
import { LegendItem } from '../../../../../../common/chart/legend/LegendItem'
import { getUnique } from '../../../../../../helpers/common'
import TooltipExpression from '../../../common/tooltipExpression'
import {
  getNoDataComponent,
  getStyleHeightContainer,
  handleIconDownload,
} from '../../../helper'
import classes from '../../../index.module.scss'
import { selectDataViewByTrend } from '../store/slice'
import Chart from './Chart'

const converData = (data, key) => {
  return data.map((item) => ({
    year: item.year,
    value: item[key.VALUE],
    valueAvg: item[key.VALUE_AVG],
  }))
}

const Trend = ({ isDynamicReport, isDownloadComponent, id }) => {
  const chartId = useRef(getUnique())

  const data = useSelector(selectDataViewByTrend)

  const isNoData = !data?.length

  useEffect(() => {
    handleIconDownload(id, isNoData)
  }, [isNoData])

  if (isNoData) {
    return getNoDataComponent(isDynamicReport)
  }

  return (
    <div
      style={{
        height: getStyleHeightContainer(isDynamicReport, isNoData, 380),
      }}
      className={classes.assetManageEffiContainer}
    >
      <div className="h100 dFlex">
        <Chart
          data={converData(data, daysSaleKey)}
          label="financialAnalysis.DAYS_SALE"
          operator="+"
          chartId={chartId.current}
          valueCode={daysSaleKey.VALUE}
          valueAvgCode={daysSaleKey.VALUE_AVG}
          isDynamicReport={isDynamicReport || isDownloadComponent}
          tooltipContent={
            <TooltipExpression
              expressions={[
                {
                  text: I18n.t('financialAnalysis.DAYS_SALE_1'),
                  operator: '=',
                },
                {
                  text: I18n.t('financialAnalysis.DAYS_SALE_2'),
                  operator: 'x',
                  isDivide: true,
                  textUnder: I18n.t('financialAnalysis.DAYS_SALE_3'),
                },
                {
                  text: I18n.t('financialAnalysis.DAYS_SALE_4'),
                },
              ]}
            />
          }
        />
        <Chart
          data={converData(data, daysInventoryKey)}
          label="financialAnalysis.DAYS_INVENTORY"
          operator="-"
          chartId={chartId.current}
          valueCode={daysInventoryKey.VALUE}
          valueAvgCode={daysInventoryKey.VALUE_AVG}
          isDynamicReport={isDynamicReport || isDownloadComponent}
          tooltipContent={
            <TooltipExpression
              expressions={[
                {
                  text: I18n.t('financialAnalysis.DAYS_INVENTORY_1'),
                  operator: '=',
                },
                {
                  text: I18n.t('financialAnalysis.DAYS_INVENTORY_2'),
                  operator: 'x',
                  isDivide: true,
                  textUnder: I18n.t('financialAnalysis.DAYS_INVENTORY_3'),
                },
                {
                  text: I18n.t('financialAnalysis.DAYS_INVENTORY_4'),
                },
              ]}
            />
          }
        />
        <Chart
          data={converData(data, daysPayableKey)}
          label="financialAnalysis.DAYS_PAYABLE"
          operator="="
          chartId={chartId.current}
          valueCode={daysPayableKey.VALUE}
          valueAvgCode={daysPayableKey.VALUE_AVG}
          isDynamicReport={isDynamicReport || isDownloadComponent}
          tooltipContent={
            <TooltipExpression
              expressions={[
                {
                  text: I18n.t('financialAnalysis.DAYS_PAYABLE_1'),
                  operator: '=',
                },
                {
                  text: I18n.t('financialAnalysis.DAYS_PAYABLE_2'),
                  operator: 'x',
                  isDivide: true,
                  textUnder: I18n.t('financialAnalysis.DAYS_PAYABLE_3'),
                },
                {
                  text: I18n.t('financialAnalysis.DAYS_PAYABLE_4'),
                },
              ]}
            />
          }
        />
        <Chart
          data={converData(data, daysConversionKey)}
          label="financialAnalysis.CASH_CONVERSION"
          operator={<>&nbsp;</>}
          chartId={chartId.current}
          valueCode={daysConversionKey.VALUE}
          valueAvgCode={daysConversionKey.VALUE_AVG}
          isDynamicReport={isDynamicReport || isDownloadComponent}
          isLastChart
          tooltipContent={
            <TooltipExpression
              style={{ alignItems: 'unset' }}
              expressions={[
                {
                  text: I18n.t('financialAnalysis.CASH_CONVERSION_1'),
                  operator: '=',
                  styleContainer: { alignItems: 'unset' },
                },
                {
                  text: (
                    <>
                      {I18n.t('financialAnalysis.CASH_CONVERSION_2')}
                      <br />
                      +&nbsp;{I18n.t('financialAnalysis.CASH_CONVERSION_3')}
                      <br />
                      -&nbsp;{I18n.t('financialAnalysis.CASH_CONVERSION_4')}
                    </>
                  ),
                  styleText: { textAlign: 'unset' },
                  styleContainer: { alignItems: 'unset' },
                },
              ]}
            />
          }
        />
      </div>
      <div className="pt1 dFlex alignItemsCenter justifyContentCenter">
        <div className="dFlex">
          <LegendItem
            isI18n
            color="#006AA6"
            title="financialAnalysis.COMPANY"
            dataKey="value"
            chartId={chartId.current}
          />
          <div className="ml4">
            <LegendItem
              isI18n
              color="#FFCD60"
              title="financialAnalysis.INDUSTRY_AVERAGE"
              dataKey="valueAvg"
              chartId={chartId.current}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default Trend
